import { useState, useCallback, useEffect, useRef } from "react";
import type { Run, BOMItem, RunStatus, ProductUnit, RunScale, Project } from "@/types/chemistry";
import { generateMockSupplierOffers } from "@/data/mockSuppliers";

const STORAGE_KEY = "chem-route-runs";
const generateId = () => crypto.randomUUID();

const loadRuns = (): Run[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    let runs: Run[] = raw ? JSON.parse(raw) : [];
    // One-time cleanup: remove "biou" run
    const before = runs.length;
    runs = runs.filter(r => !r.title.toLowerCase().includes("biou"));
    if (runs.length !== before) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(runs));
    }
    return runs;
  } catch {
    return [];
  }
};

const saveRuns = (runs: Run[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(runs));
};

/** Back-calculate per-step target dates from deadline. Walks backwards: last step ends at deadline. */
export function computeStepTargetDates(
  project: Project,
  selectedStepIds: string[],
  deadline: string | undefined,
  stepOverrides?: Record<string, { yield?: number; stepTimeDays?: number }>
): Record<string, string> {
  const dates: Record<string, string> = {};
  if (!deadline) return dates;

  const orderedSteps = project.steps.filter(s => selectedStepIds.includes(s.id));
  let cursor = new Date(deadline);

  // Walk backwards from last step
  for (let i = orderedSteps.length - 1; i >= 0; i--) {
    const step = orderedSteps[i];
    const overrideDays = stepOverrides?.[step.id]?.stepTimeDays;
    const stepTimeDays = overrideDays ?? (step.stepTime ? parseInt(step.stepTime) || 0 : 0);
    
    // Target date for this step = cursor (when materials must arrive by)
    const startDate = new Date(cursor);
    startDate.setDate(startDate.getDate() - stepTimeDays);
    dates[step.id] = startDate.toISOString().split("T")[0];
    cursor = startDate;
  }
  return dates;
}

export function generateBOM(
  project: Project,
  productAmount: number,
  assumedYield: number,
  selectedStepIds?: string[],
  stepOverrides?: Record<string, { yield?: number; stepTimeDays?: number }>,
  deadline?: string
): BOMItem[] {
  const items: BOMItem[] = [];
  const seen = new Map<string, BOMItem>();

  const stepIds = selectedStepIds || project.steps.map(s => s.id);
  const steps = project.steps.filter(s => stepIds.includes(s.id));

  // Compute target dates for lead-time comparison
  const targetDates = computeStepTargetDates(project, stepIds, deadline, stepOverrides);

  steps.forEach((step) => {
    const stepYield = stepOverrides?.[step.id]?.yield ?? assumedYield;
    const yieldFactor = 100 / Math.max(stepYield, 1);

    const chemicals = [
      ...step.reactants.filter((c) => c.formula.trim()),
      ...step.reagents.filter((c) => c.formula.trim()),
    ];

    chemicals.forEach((c) => {
      const key = c.casNumber || c.formula;
      if (seen.has(key)) {
        const existing = seen.get(key)!;
        existing.quantity += productAmount * yieldFactor;
        existing.stepIds.push(step.id);
      } else {
        const item: BOMItem = {
          id: generateId(),
          materialName: c.formula,
          chemicalName: c.name || "",
          casNumber: c.casNumber || "",
          quantity: Math.round(productAmount * yieldFactor * 100) / 100,
          unit: "g",
          stepIds: [step.id],
          status: "not_ordered",
          pricePerUnit: c.price || undefined,
          deliveryDays: undefined,
          co2eq: c.co2eq || undefined,
          molarWeight: c.molarWeight || undefined,
          targetDate: targetDates[step.id] || undefined,
          supplierOffers: generateMockSupplierOffers(c.formula, c.casNumber || ""),
        };
        seen.set(key, item);
        items.push(item);
      }
    });
  });

  return items;
}

export function useRuns() {
  const [runs, setRuns] = useState<Run[]>(loadRuns);
  const autoSaveRef = useRef<NodeJS.Timeout | null>(null);

  const persist = useCallback((updated: Run[]) => {
    setRuns(updated);
    saveRuns(updated);
  }, []);

  const createRun = useCallback((
    projectId: string,
    title: string,
    productAmount: number,
    productUnit: ProductUnit,
    scale: RunScale,
    project: Project,
    opts?: { deadline?: string; startDate?: string; notes?: string; customScale?: string; assumedYield?: number; selectedStepIds?: string[]; stepOverrides?: Record<string, { yield?: number; stepTimeDays?: number; startDate?: string; endDate?: string }>; compoundAmounts?: Record<string, number> }
  ) => {
    const assumedYield = opts?.assumedYield ?? 80;
    const selectedStepIds = opts?.selectedStepIds || project.steps.map(s => s.id);
    const rawOverrides = opts?.stepOverrides;
    // Map step startDate to deadline for BOM target dates
    const stepOverrides: Record<string, { yield?: number; stepTimeDays?: number; deadline?: string }> | undefined = rawOverrides
      ? Object.fromEntries(
          Object.entries(rawOverrides).map(([id, o]) => [id, {
            yield: o.yield,
            stepTimeDays: o.stepTimeDays,
            deadline: o.startDate || undefined,
          }])
        )
      : undefined;
    const bomItems = generateBOM(project, productAmount, assumedYield, selectedStepIds, stepOverrides, opts?.deadline);
    const run: Run = {
      id: generateId(),
      projectId,
      title,
      productAmount,
      productUnit,
      scale,
      customScale: opts?.customScale,
      deadline: opts?.deadline,
      startDate: opts?.startDate || new Date().toISOString().split("T")[0],
      status: "draft",
      notes: opts?.notes || "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      bomItems,
      assumedYield,
      selectedStepIds,
      stepOverrides,
      compoundAmounts: opts?.compoundAmounts,
    };
    persist([...runs, run]);
    return run;
  }, [runs, persist]);

  const updateRun = useCallback((runId: string, updates: Partial<Run>) => {
    persist(runs.map(r => r.id === runId ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r));
  }, [runs, persist]);

  const deleteRun = useCallback((runId: string) => {
    persist(runs.filter(r => r.id !== runId));
  }, [runs, persist]);

  const getRun = useCallback((runId: string) => {
    return runs.find(r => r.id === runId);
  }, [runs]);

  const getRunsForProject = useCallback((projectId: string) => {
    return runs.filter(r => r.projectId === projectId);
  }, [runs]);

  const updateBOMItem = useCallback((runId: string, itemId: string, updates: Partial<BOMItem>) => {
    persist(runs.map(r => {
      if (r.id !== runId) return r;
      return {
        ...r,
        updatedAt: new Date().toISOString(),
        bomItems: r.bomItems.map(b => b.id === itemId ? { ...b, ...updates } : b),
      };
    }));
  }, [runs, persist]);

  const regenerateBOM = useCallback((runId: string, project: Project) => {
    persist(runs.map(r => {
      if (r.id !== runId) return r;
      const newItems = generateBOM(project, r.productAmount, r.assumedYield, r.selectedStepIds, r.stepOverrides, r.deadline);
      // Preserve supplier selections from existing BOM items by matching on materialName+casNumber
      const oldByKey = new Map<string, BOMItem>();
      r.bomItems.forEach(item => {
        const key = `${item.casNumber || ""}::${item.materialName}`;
        oldByKey.set(key, item);
      });
      const mergedItems = newItems.map(item => {
        const key = `${item.casNumber || ""}::${item.materialName}`;
        const old = oldByKey.get(key);
        if (old && old.supplierName) {
          return {
            ...item,
            supplierId: old.supplierId,
            supplierName: old.supplierName,
            status: old.status,
            pricePerUnit: old.pricePerUnit,
            packageSize: old.packageSize,
            selectedCost: old.selectedCost,
            earliestDelivery: old.earliestDelivery,
            co2eq: old.co2eq,
            supplierOffers: old.supplierOffers,
          };
        }
        return item;
      });
      return {
        ...r,
        updatedAt: new Date().toISOString(),
        bomItems: mergedItems,
      };
    }));
  }, [runs, persist]);

  // Auto-save draft hook
  const scheduleAutoSave = useCallback((runId: string, updates: Partial<Run>) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      updateRun(runId, updates);
    }, 30000);
  }, [updateRun]);

  useEffect(() => {
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, []);

  return {
    runs,
    createRun,
    updateRun,
    deleteRun,
    getRun,
    getRunsForProject,
    updateBOMItem,
    regenerateBOM,
    scheduleAutoSave,
  };
}
